import { useState, useEffect } from "react";
import { fetchCosts } from "../utils/api";

const SERVICE_ICONS = {
  "Bedrock":     "🤖",
  "CloudFront":  "🌐",
  "Lambda":      "λ",
  "API Gateway": "🔀",
  "S3":          "🪣",
  "CloudWatch":  "📊",
  "Route 53":    "🔭",
  "ACM":         "🔒",
  "KMS":         "🔑",
  "Tax":         "🧾",
};

const BAR_COLORS = ["#38BDF8", "#C084FC", "#34D399", "#FB923C", "#F472B6", "#FBBF24", "#60A5FA"];

export default function CostPanel({ onClose, apiKey = "" }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    fetchCosts(apiKey)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [apiKey]);

  const overlay = {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 16,
  };
  const panel = {
    background: "#0d0e1a", border: "1px solid #1e2030", borderRadius: 18,
    padding: "28px 24px", maxWidth: 520, width: "100%",
    fontFamily: "'IBM Plex Mono', monospace", color: "#e2e8f0",
    maxHeight: "90vh", overflowY: "auto",
  };

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={panel}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: 3, color: "#475569", textTransform: "uppercase", marginBottom: 4 }}>
              AWS Cost Explorer
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#e2e8f0" }}>This Month's Spend</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#475569", fontSize: 20, cursor: "pointer", padding: 4 }}>✕</button>
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#475569" }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>⏳</div>
            Fetching live cost data…
          </div>
        )}

        {error && (
          <div style={{ background: "#1a0a0a", border: "1px solid #7f1d1d", borderRadius: 10, padding: "14px 16px", color: "#f87171", fontSize: 12 }}>
            <strong>Error:</strong> {error}
            <div style={{ color: "#64748b", marginTop: 6, fontSize: 11 }}>
              Make sure the Lambda IAM role has <code>ce:GetCostAndUsage</code> permission.
            </div>
          </div>
        )}

        {data && (
          <>
            {/* Total */}
            <div style={{ background: "#070810", border: "1px solid #151728", borderRadius: 14, padding: "18px 20px", marginBottom: 20, textAlign: "center" }}>
              <div style={{ fontSize: 36, fontWeight: 900, color: "#38BDF8", letterSpacing: -1 }}>
                ${data.total.toFixed(4)}
              </div>
              <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>{data.currency} · {data.period}</div>
            </div>

            {/* Service bars */}
            {data.services.length === 0 ? (
              <div style={{ textAlign: "center", color: "#475569", padding: "20px 0", fontSize: 13 }}>
                No charges yet this month 🎉
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {data.services.map((s, i) => {
                  const pct = data.total > 0 ? (s.amount / data.total) * 100 : 0;
                  const c   = BAR_COLORS[i % BAR_COLORS.length];
                  return (
                    <div key={s.name}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 12 }}>
                        <span style={{ color: "#94a3b8" }}>
                          {SERVICE_ICONS[s.name] || "☁️"} {s.name}
                        </span>
                        <span style={{ color: c, fontWeight: 700 }}>${s.amount.toFixed(4)}</span>
                      </div>
                      <div style={{ height: 6, background: "#1e2030", borderRadius: 99 }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: c, borderRadius: 99, transition: "width 0.6s ease" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Footer */}
            <div style={{ marginTop: 20, padding: "12px 14px", background: "#070810", borderRadius: 10, fontSize: 11, color: "#334155" }}>
              Live data via AWS Cost Explorer · Updates daily · Month-to-date
            </div>
          </>
        )}
      </div>
    </div>
  );
}
